import { forwardRef, type HTMLAttributes, type InputHTMLAttributes, type SelectHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/format";

export { Button } from "./Button";

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { tone?: "paper" | "ink" }>(
  function Card({ tone = "paper", className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "rounded-md",
          tone === "paper"
            ? "bg-paper-soft/60 border border-[rgba(42,36,26,0.12)] backdrop-blur-sm"
            : "bg-ink-card text-paper border border-white/5",
          className,
        )}
        {...props}
      />
    );
  },
);

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "w-full bg-transparent border border-[rgba(42,36,26,0.2)] rounded px-3.5 py-2.5 text-[0.95rem] text-ink placeholder:text-ink-faint focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/15 transition-colors",
        props.className,
      )}
    />
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        "w-full bg-transparent border border-[rgba(42,36,26,0.2)] rounded px-3.5 py-2.5 text-[0.95rem] text-ink focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/15 transition-colors",
        props.className,
      )}
    />
  );
}

export function Label({ children, className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label className={cn("block text-xs font-medium text-ink-soft mb-1.5", className)} {...props}>
      {children}
    </label>
  );
}

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: "neutral" | "terracotta" | "good" | "warn";
  className?: string;
}) {
  const tones = {
    neutral: "bg-[rgba(42,36,26,0.08)] text-ink-soft border-[rgba(42,36,26,0.12)]",
    terracotta: "bg-terracotta text-paper border-terracotta",
    good: "bg-[rgba(56,102,65,0.12)] text-[#386641] border-[rgba(56,102,65,0.25)]",
    warn: "bg-[rgba(184,71,44,0.12)] text-terracotta border-[rgba(184,71,44,0.25)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 py-0.5 text-[0.7rem] font-medium rounded-full border",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn("border-0 h-px bg-[rgba(42,36,26,0.15)]", className)} />;
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "font-mono uppercase text-[0.7rem] tracking-eyebrow text-ink-faint",
        className,
      )}
    >
      {children}
    </div>
  );
}
